import { useCallback, useEffect, useRef, useState } from "react";
import { apiUrl } from "../lib/api";
import { markDirty } from "../lib/dirtyViews";
import { queryKeys } from "../lib/queryKeys";
import { queryClient } from "../lib/queryClient";
import {
  useOrderbookStream,
  type OrderbookMessage,
  type OrderbookStreamMessage,
} from "./useOrderbookStream";
import type { SeasonStanding } from "../types/market";
import {
  shouldResyncOnDocumentVisibleChange,
  shouldResyncOnStreamReady,
  type OrderbookStreamStatus,
} from "./orderbookResyncPolicy";
import { useApiAuth } from "./useApiAuth";
import { applyMarketLastTradeToStandings } from "../lib/marketLastTradeCache";

type UsePoolOrderbookCoordinatorArgs = {
  competitionCode: string | null;
  seasonCode: string | null;
  poolCode: string | null;
  enabled?: boolean;
};

type OrderbookWebSocketTicketResponse = {
  ticket?: string | null;
};

function updateMarketCache(previous: SeasonStanding[] | undefined, message: OrderbookMessage) {
  if (!previous) return previous;

  const quoteUpdated = previous.map((team) => {
    if (team.season_team_id !== message.season_team_id) return team;

    const bid = message.top_of_book?.bid ?? null;
    const ask = message.top_of_book?.ask ?? null;

    return {
      ...team,
      top_bid_order_id: bid?.trade_order_id ?? null,
      top_bid_price: bid?.price ?? null,
      top_bid_quantity: bid?.quantity ?? null,
      top_bid_created_at: bid?.created_at ?? null,
      top_bid_trader_code: bid?.trader_code ?? null,
      top_bid_participant_type: bid?.participant_type ?? null,
      top_bid_participant_display_name: bid?.participant_display_name ?? null,
      top_bid_participant_profile: bid?.participant_profile ?? null,
      top_bid_participant_profile_description: bid?.participant_profile_description ?? null,
      top_ask_order_id: ask?.trade_order_id ?? null,
      top_ask_price: ask?.price ?? null,
      top_ask_quantity: ask?.quantity ?? null,
      top_ask_created_at: ask?.created_at ?? null,
      top_ask_trader_code: ask?.trader_code ?? null,
      top_ask_participant_type: ask?.participant_type ?? null,
      top_ask_participant_display_name: ask?.participant_display_name ?? null,
      top_ask_participant_profile: ask?.participant_profile ?? null,
      top_ask_participant_profile_description: ask?.participant_profile_description ?? null,
    } satisfies SeasonStanding;
  });

  return applyMarketLastTradeToStandings(quoteUpdated, message);
}

export function usePoolOrderbookCoordinator({
  competitionCode,
  seasonCode,
  poolCode,
  enabled = true,
}: UsePoolOrderbookCoordinatorArgs) {
  const { acquireToken } = useApiAuth();
  const [isDocumentVisible, setIsDocumentVisible] = useState(() => {
    if (typeof document === "undefined") {
      return true;
    }
    return document.visibilityState === "visible";
  });
  const [latestOrderbookMessage, setLatestOrderbookMessage] = useState<OrderbookMessage | null>(null);
  const previousDocumentVisibleRef = useRef(isDocumentVisible);
  const previousStreamStatusRef = useRef<OrderbookStreamStatus>("idle");
  const hasSeenReadyRef = useRef(false);

  const refetchSettlementDrivenQueries = useCallback(() => {
    if (!poolCode) return;

    const refreshTargets = [
      queryKeys.portfolio(poolCode),
      queryKeys.leaderboard(poolCode),
      queryKeys.homeSummary(),
    ] as const;

    refreshTargets.forEach((queryKey) => {
      void queryClient.invalidateQueries({
        queryKey,
        exact: true,
        refetchType: "none",
      });
      void queryClient.refetchQueries({
        queryKey,
        exact: true,
        type: "active",
      });
    });
  }, [poolCode]);

  const resyncMarketSnapshot = useCallback(() => {
    if (!competitionCode || !seasonCode || !poolCode) {
      return;
    }

    queryClient.setQueryData(
      queryKeys.orderbookLatest(competitionCode, seasonCode, poolCode),
      null,
    );

    void queryClient.invalidateQueries({
      queryKey: queryKeys.market(competitionCode, seasonCode, poolCode),
      exact: true,
      refetchType: "none",
    });
    void queryClient.refetchQueries({
      queryKey: queryKeys.market(competitionCode, seasonCode, poolCode),
      exact: true,
      type: "active",
    });
  }, [competitionCode, poolCode, seasonCode]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    const handleVisibility = () => {
      setIsDocumentVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, []);

  useEffect(() => {
    setLatestOrderbookMessage(null);
  }, [competitionCode, seasonCode, poolCode]);

  const handleOrderbookUpdate = useCallback(
    (message: OrderbookStreamMessage) => {
      if (message.type === "pool_state_refresh") {
        if (poolCode && message.pool_code.toUpperCase() !== poolCode.toUpperCase()) {
          return;
        }
        resyncMarketSnapshot();
        refetchSettlementDrivenQueries();
        return;
      }

      if (poolCode && message.pool_code?.toUpperCase() !== poolCode.toUpperCase()) {
        return;
      }
      if (!competitionCode || !seasonCode || !poolCode) {
        return;
      }

      setLatestOrderbookMessage(message);

      queryClient.setQueryData<OrderbookMessage | null>(
        queryKeys.orderbookLatest(competitionCode, seasonCode, poolCode),
        message,
      );

      queryClient.setQueryData<SeasonStanding[]>(
        queryKeys.market(competitionCode, seasonCode, poolCode),
        (previous: SeasonStanding[] | undefined) => updateMarketCache(previous, message),
      );

      if (message.trades.length > 0) {
        markDirty(queryClient, "portfolio", poolCode, "trade_event");
        markDirty(queryClient, "leaderboard", poolCode, "trade_event");
        markDirty(queryClient, "home", poolCode, "trade_event");
      }
    },
    [
      competitionCode,
      poolCode,
      queryClient,
      refetchSettlementDrivenQueries,
      resyncMarketSnapshot,
      seasonCode,
    ],
  );

  const acquireOrderbookTicket = useCallback(async () => {
    if (!competitionCode || !seasonCode || !poolCode) {
      return null;
    }

    const token = await acquireToken();
    if (!token) {
      return null;
    }

    const response = await fetch(apiUrl("/api/orderbook/ws-ticket"), {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        competition_code: competitionCode,
        season_code: seasonCode,
        pool_code: poolCode,
      }),
    });

    if (!response.ok) {
      console.warn("Failed to acquire orderbook websocket ticket.", response.status);
      return null;
    }

    const payload: OrderbookWebSocketTicketResponse = await response.json();
    return typeof payload.ticket === "string" && payload.ticket.trim() ? payload.ticket.trim() : null;
  }, [acquireToken, competitionCode, poolCode, seasonCode]);

  const streamState = useOrderbookStream({
    competitionCode: competitionCode ?? "",
    seasonCode: seasonCode ?? "",
    poolCode: poolCode ?? "",
    onUpdate: handleOrderbookUpdate,
    acquireTicket: acquireOrderbookTicket,
    enabled: Boolean(enabled && isDocumentVisible && competitionCode && seasonCode && poolCode),
  });

  useEffect(() => {
    const previousIsVisible = previousDocumentVisibleRef.current;
    previousDocumentVisibleRef.current = isDocumentVisible;

    if (shouldResyncOnDocumentVisibleChange(previousIsVisible, isDocumentVisible)) {
      resyncMarketSnapshot();
    }
  }, [isDocumentVisible, resyncMarketSnapshot]);

  useEffect(() => {
    const previousStatus = previousStreamStatusRef.current;
    previousStreamStatusRef.current = streamState.status;

    if (
      shouldResyncOnStreamReady({
        previousStatus,
        nextStatus: streamState.status,
        hasSeenReady: hasSeenReadyRef.current,
      })
    ) {
      resyncMarketSnapshot();
    }

    if (streamState.status === "ready") {
      hasSeenReadyRef.current = true;
    }
  }, [resyncMarketSnapshot, streamState.status]);

  return {
    latestOrderbookMessage,
  };
}
